// NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
// Models
import Campaign from '@/Models/Campaign.js';
import User from '@/Models/User.js';
import QueryBuilder from '@/Models/Sarala/QueryBuilder.js';
// Stores
import { mergeModelDataInCollectionStore, mergeOrAddModelDataInCollectionStore } from '@/Stores/StoreHelpers.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getApprovalManagers() {
			return meta && meta.approval_managers;
		},

		getAccountManagers() {
			return meta && meta.account_managers;
		},

		getCampaigns(activeOnly = false, pagination = { perPage: 30, page: 1 }) {
			let campaign = new Campaign();
			if (activeOnly) {
				campaign = campaign.where('expired', false);
			}
			campaign.with(['approved_by', 'created_by']);
			return campaign.paginate(pagination.perPage, pagination.page).then((result) => {
				if (result.meta) {
					meta = result.meta;
				}
				set(collect(result.data));
			});
		},

		createCampaign(data) {
			let campaign = new Campaign();

			return campaign
				.makePersistRequest({
					url: `${campaign.resourceUrl()}`,
					method: 'POST',
					data
				})
				.then((result) => {
					return result;
				});
		},

		updateCampaign(campaignId, campaignUpdatePayload) {
			let campaign = new Campaign();

			return campaign
				.makePersistRequest({
					url: `${campaign.resourceUrl()}${campaignId}`,
					method: 'PATCH',
					data: campaignUpdatePayload
				})
				.then((result) => {
					return result;
				});
		},

		approve(campaignId) {
			let campaign = _store.firstWhere('id', campaignId);
			return campaign.approve().then((results) => {
				return results;
			});
		},

		decline(campaignId) {
			let campaign = _store.firstWhere('id', campaignId);
			return campaign.decline().then((results) => {
				return results;
			});
		},

		close(campaignId) {
			let campaign = _store.firstWhere('id', campaignId);
			return campaign.close().then((results) => {
				return results;
			});
		},

		applyCampaignUpdates(data) {
			if (data && data.id && _store.firstWhere('id', data.id.toString())) {
				mergeModelDataInCollectionStore(data, this);
			}
		},

		addCampaignFromData(data) {
			const campaign = new Campaign();
			let result = campaign.respond(data);
			if (result) {
				mergeOrAddModelDataInCollectionStore(result, this);
			}
		},

		getUserForCampaign(campaignId, pagination = { perPage: 30, page: 1 }, reload = false) {
			let campaign = _store.firstWhere('id', campaignId);
			const user = new User();
			const queryBuilder = new QueryBuilder();
			queryBuilder.paginate(pagination.perPage, pagination.page);
			queryBuilder.appendQuery('reload=' + (reload ? 1 : 0));
			return user
				.makePersistRequest({
					url: `${campaign.resourceUrl()}${campaign.id}/users${queryBuilder.getQuery()}`,
					method: 'GET'
				})
				.then((results) => {
					return results;
				});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
